<template>
  <NuxtRouteAnnouncer/>
  <v-app>
    <v-navigation-drawer
      v-model:model-value="navigationDrawer"
      color="grey-darken-3"
      elevation="6"
      border
    >
      <v-list-item
        class="py-0"
        style="padding-top: 2px !important;filter: drop-shadow(2px 2px 10px rgba(255, 255, 255, 0.5))"

      >
        <img
          src="/wiresock-logo-200x48-trans.png"
          alt="WireSock logo"
          width="200"
          height="48"
        >
      </v-list-item>
      <v-divider></v-divider>
      <v-list-item
        class="py-4"
      >
        <div class="d-flex">
          <div style="width: 55px;" class="pt-1">
            <v-avatar color="grey">{{ userNameFirstLetter }}</v-avatar>
          </div>
          <div>
            <div>{{ userName }}</div>
            <div class="text-caption">{{ userEmail }}</div>
          </div>
        </div>
      </v-list-item>
      <v-divider class="mb-4"></v-divider>

      <!-- 010-admin-impersonation: prominent exit action while impersonating -->
      <template v-if="isImpersonating">
        <v-list-item
          :loading="impersonationExiting"
          base-color="warning"
          prepend-icon="mdi-exit-to-app"
          title="Exit impersonation"
          :subtitle="impersonatedEmail ?? undefined"
          @click="handleExitImpersonation"
        ></v-list-item>
        <v-divider class="my-4"></v-divider>
      </template>

      <!--
      <v-list-item
        link
        title="Purchases"
        prepend-icon="mdi-cart"
        to="/account/purchases"
      >
      </v-list-item>
      -->
      <v-list-item
        link
        title="Licenses"
        prepend-icon="mdi-license"
        to="/account/licenses"
      ></v-list-item>
      <v-list-item
        link
        title="Downloads"
        prepend-icon="mdi-download"
        to="/account/downloads"
      ></v-list-item>
      <v-list-item
        link
        title="Payments"
        prepend-icon="mdi-credit-card"
        to="/account/payments"
      ></v-list-item>
      <v-list-item
        link
        title="Help desk"
        prepend-icon="mdi-face-agent"
        to="/account/helpdesk"
      ></v-list-item>
      <v-list-item
        link
        title="Profile"
        prepend-icon="mdi-account"
        to="/account/profile"
      ></v-list-item>

      <!-- Admin Section (only visible for admins) -->
      <template v-if="isAdmin">
        <v-divider class="my-4"></v-divider>
        <v-list-item
          link
          title="Admin Dashboard"
          prepend-icon="mdi-view-dashboard"
          to="/admin"
        ></v-list-item>
      </template>
    </v-navigation-drawer>
    <v-app-bar
      density="comfortable"
      elevation="0"
      color="grey-lighten-2"
      border
    >
      <v-app-bar-nav-icon
        v-if="!navigationDrawer"
        @click="navigationDrawer=true"
      />
      <v-app-bar-title>
        <v-icon v-if="titleIcon">
          {{ titleIcon }}
        </v-icon>
        {{ title }}
      </v-app-bar-title>
      <template #append>
        <v-btn
          variant="plain"
          to="/"
          size="small"
          class="hidden-sm-and-down"
        >
          Main website
        </v-btn>
        <v-btn
          variant="plain"
          target="_blank"
          size="small"
          href="https://wiresock.net/documentation"
          class="hidden-sm-and-down mr-2"
        >
          Documentation
        </v-btn>
        <v-btn
          @click.stop.prevent="signout()"
          class="mr-0"
        >
          Logout
          <v-icon class="ml-2">mdi-logout</v-icon>
        </v-btn>
      </template>
    </v-app-bar>

    <v-main class="mt-2 bg-grey-lighten-4">
      <v-container fluid>
        <slot/>
      </v-container>
    </v-main>
    <ClientOnly>
      <assistant-widget />
    </ClientOnly>
  </v-app>
</template>
<script
  setup
  lang="ts"
>
import { useImpersonation } from '~/composables/useImpersonation';

// AI support assistant (012-support-chat-assistant), lazy and client-only.
const AssistantWidget = defineAsyncComponent(() => import('~/components/support-assistant/AssistantWidget.vue'));

const auth = useAuth();
const route = useRoute();
const title = computed(() => {
  return route.meta.title || 'Account';
});

const navigationDrawer = ref<boolean | null>(null);

const titleIcon = computed(() => {
  return route.meta.icon;
});

async function signout() {
  await auth.signOut({ callbackUrl: '/' });
}

const userName = computed<string>(() => {
  return auth.data.value?.user?.name || '';
});

const userNameFirstLetter = computed(() => {
  return auth.data.value?.user?.name?.charAt(0).toUpperCase() || '';
});

const userEmail = computed(() => {
  return auth.data.value?.user?.email || '';
});

// 010-admin-impersonation: an admin impersonating a customer is treated by all
// downstream code as the customer (role=USER). The "Admin Dashboard" item is
// intentionally hidden in that state to keep the customer-side UX clean — the
// admin first exits impersonation via the dedicated menu item below.
const isAdmin = computed(() => {
  const user = auth.data.value?.user;
  return user?.role === 'ADMIN' && !user?.impersonationSessionId;
});

const { isImpersonating, impersonatedUser, exit: exitImpersonation } = useImpersonation();
const impersonatedEmail = computed(() => impersonatedUser.value?.email ?? null);
const impersonationExiting = ref(false);

async function handleExitImpersonation() {
  impersonationExiting.value = true;
  try {
    const { redirectTo } = await exitImpersonation();
    await navigateTo(redirectTo);
  } catch (err) {
    console.error('Failed to exit impersonation:', err);
  } finally {
    impersonationExiting.value = false;
  }
}

</script>
<style lang="css">
@media screen and (max-width: 960px) {
  body {
    padding-top: 0px;
  }
}

</style>
